import React, { lazy } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  Outlet,
} from "react-router";
import { DepotTransferPage } from "@features/depots/depotTransfer/DepotTransferPage.tsx";
import {
  SynchronizationLogListWithRouting,
  AppLayout,
  FeedbackButton,
  SmartbrokerPlusExpiredSticky,
} from "@components";
import { AssignContraAccountWithRedirectPage } from "@features/depots/assignContraAccount/AssignContraAccountWithRedirectPage.tsx";
import { FileDropHandlerWithRoutingPage } from "@features/fileDrop/FileDropHandlerWithRoutingPage.tsx";
import { ImportSuccessWithRedirectPage } from "@features/fileDrop/ImportSuccessWithRedirectPage.tsx";
import { LoginPage } from "@features/login/LoginPage.tsx";
import { SignupPage } from "@features/login/signup/SignupPage.tsx";
import { SignupConfirmationPage } from "@features/login/signup/SignupConfirmationPage.tsx";
import { EmailConfirmationPage } from "@features/login/confirm/EmailConfirmationPage.tsx";
import { ResetPasswordPage } from "@features/login/reset/ResetPasswordPage.tsx";
import { AssignPasswordPage } from "@features/login/reset/AssignPasswordPage.tsx";
import { LogoutWithRedirect } from "@features/login/logout/LogoutWithRedirect.tsx";
import { NewTransactionWizardWithRouting } from "@features/transactionWizard/NewTransactionWizardWithRouting.tsx";
import { AddTransactionWizardWithRouting } from "@features/transactionWizard/AddTransactionWizardWithRouting.tsx";
import { CreateDividendWizardWithRouting } from "@features/dividendWizard/CreateDividendWizardWithRouting.tsx";
import { CreateBatchDividendWizardWithRouting } from "@features/dividendWizard/CreateBatchDividendWizardWithRouting.tsx";
import { ManualTransactionWizardWithRouting } from "@features/transactionWizard/ManualTransactionWizardWithRouting.tsx";
import { InvestmentsPage } from "@features/investments/InvestmentsPage.tsx";
import { InvestmentByParamPage } from "@features/investments/investment/InvestmentByParamPage.tsx";
import { SingleSecurityPage } from "@features/securities/security/SingleSecurityPage.tsx";
import { DepotPage } from "@features/depots/depot/DepotPage.tsx";
import { SingleAccountByParamPage } from "@features/accounts/account/SingleAccountByParamPage.tsx";
import { AddManuallyPage } from "@features/accounts/transactions/AddManuallyPage.tsx";
import { AccountEditPage } from "@features/accounts/edit/AccountEditPage.tsx";
import { DepotEditPage } from "@features/depots/edit/DepotEditPage.tsx";
import { SettingsBankConnectionPage } from "@features/settings/SettingsBankConnections/SettingsBankConnectionPage.tsx";
import { SettingsAdvancedPage } from "@features/settings/SettingsAdvanced/SettingsAdvancedPage.tsx";
import { AppLayoutProvider } from "@providers";
import { DepotsAccountsPage } from "@features/depots/DepotsAccountsPage.tsx";
import { ScrollToTop } from "@components/ScrollToTop.tsx";
import { CsvUploadPage } from "@features/csvDrop/CsvUploadPage.tsx";
import { DashboardSelectedGroupsProvider } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";
import { DashboardPage } from "@features/dashboard/DashboardPage.tsx";
import { LegalNotePage } from "@components/LegalNote";
import { BankImportStatusProvider } from "@providers/BankImportStatusProvider/BankImportStatusProvider.tsx";
import { ErrorBoundaryRouter } from "@components/common/errorBoundaries/ErrorBoundaryRouter.tsx";
import {
  OnlyAuthenticatedRoute,
  OnlyMandatorRoute,
  NonProdRoute,
  MandatorFeatureRoute,
  withSuspense,
} from "./routes.tsx";

const ClassificationsPage = lazy(() =>
  import("@features/classifications/ClassificationsPage.tsx").then(
    (module) => ({
      default: module.ClassificationsPage,
    })
  )
);

const DividendPlannerPage = lazy(() =>
  import("@features/dividendPlanner/DividendPlannerPage.tsx").then(
    (module) => ({
      default: module.DividendPlannerPage,
    })
  )
);

const ArivaNewsPage = lazy(() =>
  import("@features/arivaNews/ArivaNewsPage.tsx").then((module) => ({
    default: module.ArivaNewsPage,
  }))
);

const ArivaCommunityPage = lazy(() =>
  import("@features/arivaCommunity/ArivaCommunityPage.tsx").then((module) => ({
    default: module.ArivaCommunityPage,
  }))
);

const MainApplicationRedirect = () => {
  window.location.href = `${import.meta.env.VITE_APP_ROOT_URL}/app`;
  return null;
};

const RootLayout = () => {
  return (
    <BankImportStatusProvider>
      <Outlet />
      <FeedbackButton />
      <SmartbrokerPlusExpiredSticky />
      <ScrollToTop />
    </BankImportStatusProvider>
  );
};

const routes = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundaryRouter />,
    children: [
      {
        path: "mainApplication",
        element: <MainApplicationRedirect />,
      },
      {
        path: "import-bank-connection/:bankSlug/legal-note",
        element: <LegalNotePage />,
      },
      {
        path: "synchronization-logs/:accountId",
        element: (
          <OnlyAuthenticatedRoute>
            <SynchronizationLogListWithRouting />
          </OnlyAuthenticatedRoute>
        ),
      },
      {
        path: "depots/:id/assign-contra-account",
        element: <AssignContraAccountWithRedirectPage />,
      },
      {
        path: "depots/:fromAccountId/transfer",
        element: <DepotTransferPage />,
      },
      {
        path: "login",
        element: (
          <NonProdRoute>
            <LoginPage />
          </NonProdRoute>
        ),
      },
      {
        path: "signup",
        element: (
          <NonProdRoute target="registration">
            <SignupPage />
          </NonProdRoute>
        ),
      },
      {
        path: "signup/confirmationRegistration",
        element: (
          <NonProdRoute>
            <SignupConfirmationPage />
          </NonProdRoute>
        ),
      },
      {
        path: "confirmation/email",
        element: (
          <NonProdRoute>
            <EmailConfirmationPage />
          </NonProdRoute>
        ),
      },
      {
        path: "reset/password",
        element: (
          <NonProdRoute>
            <ResetPasswordPage />
          </NonProdRoute>
        ),
      },
      {
        path: "reset/assign-password",
        element: (
          <NonProdRoute>
            <AssignPasswordPage />
          </NonProdRoute>
        ),
      },
      {
        path: "logout",
        element: <LogoutWithRedirect />,
      },
      {
        path: "settings",
        element: (
          <OnlyAuthenticatedRoute>
            <Outlet />
          </OnlyAuthenticatedRoute>
        ),
        children: [
          {
            path: "bankConnections/:apiProvider/:id",
            element: <SettingsBankConnectionPage />,
          },
          {
            path: "advanced",
            element: <SettingsAdvancedPage />,
          },
        ],
      },
      {
        path: "",
        element: (
          <OnlyAuthenticatedRoute>
            <AppLayoutProvider>
              <AppLayout />
            </AppLayoutProvider>
          </OnlyAuthenticatedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <DashboardSelectedGroupsProvider>
                <DashboardPage />
              </DashboardSelectedGroupsProvider>
            ),
          },
          {
            path: "investments",
            children: [
              {
                index: true,
                element: <InvestmentsPage />,
              },
              {
                path: "file-drop",
                element: <FileDropHandlerWithRoutingPage />,
              },
              {
                path: "csv-drop",
                element: <CsvUploadPage />,
              },
              {
                path: "file-drop-success/:depotSynchronizationLogId",
                element: <ImportSuccessWithRedirectPage />,
              },
              {
                path: ":investmentId/dividend-wizard/create/batch",
                element: <CreateBatchDividendWizardWithRouting />,
              },
              {
                path: ":investmentId/dividend-wizard/create",
                element: <CreateDividendWizardWithRouting />,
              },
              {
                path: ":investmentId",
                element: (
                  <MandatorFeatureRoute
                    feature="investmentDetailPage"
                    redirectTo="/investments"
                  >
                    <InvestmentByParamPage />
                  </MandatorFeatureRoute>
                ),
              },
            ],
          },
          {
            path: "depots-accounts",
            element: <DepotsAccountsPage />,
          },
          {
            path: "depots",
            children: [
              {
                index: true,
                element: <Navigate to="/depots-accounts" replace />,
              },
              {
                path: ":depotId/edit",
                element: <DepotEditPage />,
              },
              {
                path: ":depotId",
                element: <DepotPage />,
              },
              {
                path: ":depotId/investments/:investmentId",
                element: (
                  <MandatorFeatureRoute
                    feature="investmentDetailPage"
                    redirectTo="/depots-accounts"
                  >
                    <InvestmentByParamPage />
                  </MandatorFeatureRoute>
                ),
              },
              {
                path: ":depotId/csv-drop",
                element: <CsvUploadPage />,
              },
              {
                path: ":accountId/file-drop",
                element: <FileDropHandlerWithRoutingPage />,
              },
              {
                path: ":accountId/file-drop-success/:depotSynchronizationLogId",
                element: <ImportSuccessWithRedirectPage />,
              },
            ],
          },
          {
            path: "accounts",
            children: [
              {
                index: true,
                element: <Navigate to="/depots-accounts" replace />,
              },
              {
                path: ":accountId/transactions/add-manually",
                element: <AddManuallyPage />,
              },
              {
                path: ":accountId/edit",
                element: <AccountEditPage />,
              },
              {
                path: ":accountId",
                element: <SingleAccountByParamPage />,
              },
            ],
          },
          {
            path: "classifications",
            element: withSuspense(ClassificationsPage),
          },
          {
            path: "dividend-planner",
            element: withSuspense(DividendPlannerPage),
          },
          {
            path: "news",
            element: (
              <OnlyMandatorRoute mandators={["ariva_de"]}>
                {withSuspense(ArivaNewsPage)}
              </OnlyMandatorRoute>
            ),
          },
          {
            path: "community",
            element: (
              <OnlyMandatorRoute mandators={["ariva_de"]}>
                {withSuspense(ArivaCommunityPage)}
              </OnlyMandatorRoute>
            ),
          },
          {
            path: "securities",
            element: (
              <MandatorFeatureRoute feature="securityDetailPage">
                <SingleSecurityPage />
              </MandatorFeatureRoute>
            ),
          },
          {
            path: "new-transaction-wizard",
            element: <NewTransactionWizardWithRouting />,
          },
          {
            path: "manual-transaction-wizard",
            element: <ManualTransactionWizardWithRouting />,
          },
          {
            path: "add-transaction-wizard/:id",
            element: <AddTransactionWizardWithRouting />,
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: import.meta.env.VITE_PUBLIC_URL,
});

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
